/**
 * Attaches an event listener that will fire once and then autodelete
 *
 * @param el
 * @param eventName
 * @param handler
 */
export function one(
  el: HTMLElement,
  eventName: keyof HTMLElementEventMap,
  handler: () => void,
) {
  if (typeof el === "undefined") {
    return;
  }

  addEventListener(el, eventName, function justOnce() {
    handler();
    removeEventListener(el, eventName, justOnce);
  });
}

export function getData(el: HTMLElement, name: string) {
  if (typeof el === "undefined") {
    return "";
  }

  if (typeof el.dataset === "undefined") {
    return el.getAttribute("data-" + name);
  }

  return el.dataset[name];
}

export function addEventListener(
    el: HTMLElement | Window,
    eventName: string,
    handler: (event: Event) => void,
) {
  if (typeof el === "undefined" || el === null) {
    return;
  }

  el.addEventListener(eventName, handler);
}

export function removeEventListener(
  el: HTMLElement,
  eventName: keyof HTMLElementEventMap,
  handler: (event: Event) => void,
) {
  if (typeof el === "undefined") {
    return;
  }

  el.removeEventListener(eventName, handler);
}

export function addClass(el: HTMLElement|Element, className: string) {
  if (typeof el === "undefined") {
    return;
  }

  if (el.classList) {
    el.classList.add(className);
  } else {
    el.className += " " + className;
  }
}

export function removeClass(el: Element, className: string | string[]) {
  if (typeof el === "undefined") {
    return;
  }

  if (el.classList) {
    if (typeof className === "string") {
      el.classList.remove(className);
    } else {
      for (const name of className) {
        el.classList.remove(name);
      }
    }
  } else {
    if (typeof className === "string") {
      el.className = el.className.replace(
        new RegExp(
          "(^|\\b)" + className.split(" ").join("|") + "(\\b|$)",
          "gi",
        ),
        " ",
      );
    } else {
      for (const name of className) {
        el.className = el.className.replace(
          new RegExp("(^|\\b)" + name.split(" ").join("|") + "(\\b|$)", "gi"),
          " ",
        );
      }
    }
  }
}

export function show(el: HTMLElement, style: string = "block") {
  if (typeof el === "undefined") {
    return;
  }

  el.style.display = style;
}

export function hide(el: HTMLElement) {
  if (typeof el === "undefined") {
    return;
  }

  el.style.display = "none";
}

export function hideAll(elementCollection: HTMLElement[]|NodeList) {
  for (const item of elementCollection) {
    hide(item);
  }
}

export function showAll(
  elementCollection: HTMLElement[]|NodeList,
  style: string = "block",
) {
  elementCollection.forEach((item) => {
    show(item, style);
  });
}

export function remove(el: HTMLElement) {
  if (typeof el === "undefined") {
    return;
  }

  if (el !== null) {
    el.parentNode.removeChild(el);
  }
}

export function removeAll(elementCollection: HTMLElement[]) {
  for (let n = elementCollection.length - 1; n >= 0; n--) {
    remove(elementCollection[n]);
  }
}

/**
 * Find the next Sibling to the element. Returns null if there is no other sibling.
 *
 * @param {Node} element
 * @return {Node | null}
 */
export function getNextSibling(element: HTMLElement): Node | null {
  if (typeof element === "undefined") {
    return null;
  }

  let e = element.nextSibling;
  while (e && 1 !== e.nodeType) {
    e = e.nextSibling;
  }
  return e;
}

export function triggerEvent(el: HTMLElement|Window, eventName: string, data: any) {
  if (typeof el === "undefined") {
    return;
  }

  let event;

  // @ts-ignore
  if (window.CustomEvent) {
    // @ts-ignore
    if (typeof window.CustomEvent === "function") {
      event = new CustomEvent(eventName, { detail: data });
    } else {
      event = document.createEvent("CustomEvent");
      event.initCustomEvent(eventName, false, false, data);
    }
  } else {
    event = document.createEvent("CustomEvent");
    event.initCustomEvent(eventName, true, true, data);
  }

  el.dispatchEvent(event);
}

/**
 * Uses an asynchronous GET for JSON data
 */
export function getJson(url: string, data: any = {}) {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    const query = Object.keys(data)
      .map((key) => key + "=" + encodeURIComponent(data[key]))
      .join("&");

    request.open("GET", url + "?" + query, true);

    request.onload = () => {
      if (request.status >= 200 && request.status < 400) {
        resolve(JSON.parse(request.responseText));
      } else {
        reject(request.responseText);
      }
    };

    request.onerror = () => {
      reject(request.responseText);
    };

    request.send();
  });
}

/**
 * Waits for a few seconds before resolving the promise
 */
export function wait(seconds: number): Promise<any> {
  return new Promise((resolve) => {
    setTimeout(resolve, seconds * 1000);
  });
}

/**
 * Turns a string into HTML.
 *
 * Warning: it does not work for list element or other stuff that does not go natively into a DIV!
 *
 * @param text
 * @return {Node}
 */
export function stringToHtml(text: string): Element {
  let wrapper;

  if (text.indexOf("<li") === 0) {
    wrapper = document.createElement("ul");
  } else {
    wrapper = document.createElement("div");
  }
  wrapper.innerHTML = text;
  return wrapper.firstElementChild;
}

/**
 * Checks if an element has a single class
 *
 * Cannot check if it has multiple classes!
 *
 * @param el
 * @param className
 * @return {boolean}
 */
export function hasClass(el: HTMLElement|Element, className: string): boolean {
  if (el.classList) {
    return el.classList.contains(className);
  }

  return new RegExp("(^| )" + className + "( |$)", "gi").test(el.className);
}

/**
 * Toggles as single classname on en element
 *
 * @param el
 * @param className
 */
export function toggleClass(el: HTMLElement, className: string) {
  if (hasClass(el, className)) {
    removeClass(el, className);
  } else {
    addClass(el, className);
  }
}

/**
 * Cross Browser Width and Height checker
 *
 * @return {{width: number, height: number}}
 */
export function getScreenSize(): { width: number; height: number } {
  const w = window;
  const d = document;
  const e = d.documentElement;
  const g = d.body || d.getElementsByTagName("body")[0];
  const width = w.innerWidth || e.clientWidth || g.clientWidth;
  const height = w.innerHeight || e.clientHeight || g.clientHeight;

  return { width, height };
}

/**
 * Basically the jQuery finder in simple
 *
 * Returns either a single node (if we are looking for an id) or a NodeList, even if we are only searching for
 * a single thing.
 */
export function $(query: string) {
  if (query.match(/^#\w+$/)) {
    return document.getElementById(query.substr(1));
  }

  if (query.match(/^\.\w+$/)) {
    return document.getElementsByClassName(query.substr(1));
  }

  if (query.match(/^\w+$/)) {
    return document.getElementsByName(query);
  }

  return document.querySelectorAll(query);
}

/**
 * Throttle a function so it can be only called once in limit Microseconds
 */
export const throttle = (func: () => void, limit: number) => {
  let inThrottle: boolean = false;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

/**
 * Debounce a function, so it only is called after delay. Resets delay when called multiple times.
 */
export const debounce = (func: () => void, delay: number) => {
  let inDebounce: NodeJS.Timeout;
  return function() {
    const context = this;
    const args = arguments;
    clearTimeout(inDebounce);
    inDebounce = setTimeout(() => func.apply(context, args), delay);
  };
};

/**
 * Shuffle an array using fisher/yates
 *
 * @param array
 */
export function shuffleArray(array: any[]) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
}
